import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Download, Cloud, CloudOff, RefreshCw, Check, X,
  AlertTriangle, Wifi, WifiOff, Database, Clock, FileText, Loader2, Bell, Zap
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import {
  isOfflineModeValid,
  saveOfflineSession,
  clearOfflineSession,
  saveBillsOffline,
  saveAdminFeeOffline,
  getPendingOfflinePayments,
  markPaymentSynced,
  getOfflineStats,
  getTodayDateString,
  saveAuthTokenForSync,
  notifyServiceWorkerOnline,
  debugIndexedDB
} from "@/utils/offlineDB";
import {
  getNotificationStatus,
  requestNotificationPermission,
  sendDownloadNotification,
  sendSyncSuccessNotification,
  sendSyncPartialNotification
} from "@/utils/notifications";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuthHeader() {
  const token = localStorage.getItem("user_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const formatCurrency = (amount) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(amount || 0);

export default function OfflineMode() {
  const navigate = useNavigate();
  const isLoggedIn = !!localStorage.getItem("user_token");
  const userName = localStorage.getItem("user_name") || "";
  const userRole = localStorage.getItem("user_role") || "";
  
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [offlineStatus, setOfflineStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [notificationStatus, setNotificationStatus] = useState({ supported: false, permission: 'default' });
  
  // Check notification status on mount
  useEffect(() => {
    const status = getNotificationStatus();
    setNotificationStatus(status);
  }, []);

  // Save auth token for background sync when logged in
  useEffect(() => {
    const token = localStorage.getItem("user_token");
    if (token) {
      saveAuthTokenForSync(token);
    }
  }, []);

  // Note: GlobalAutoSync component handles the periodic polling globally.
  // Auto-Sync is ALWAYS ACTIVE for petugas and cannot be disabled.

  // Check online status
  useEffect(() => {
    const handleOnline = () => {
      setOnline(true);
      // Notify service worker about online status
      notifyServiceWorkerOnline();
      // GlobalAutoSync will handle the auto-sync when online
    };
    
    const handleOffline = () => setOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Listen for sync complete messages from service worker
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'SYNC_COMPLETE') {
        const { synced, failed, balance_after } = event.data;
        
        // Update balance in localStorage
        if (balance_after !== undefined) {
          localStorage.setItem("user_balance", balance_after);
        }
        
        // Show toast
        if (synced > 0) {
          toast.success(`Auto-sync: ${synced} pembayaran berhasil disinkronkan`);
        }
        if (failed > 0) {
          toast.error(`Auto-sync: ${failed} pembayaran gagal`);
        }
        
        // Reload status
        loadStatus();
      }
    };
    
    navigator.serviceWorker?.addEventListener('message', handleMessage);
    
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
    };
  }, []);
  
  // Check if user is petugas
  useEffect(() => {
    if (!isLoggedIn) {
      toast.error("Silakan login terlebih dahulu");
      navigate("/login");
      return;
    }
    
    if (userRole.toLowerCase() !== "petugas") {
      toast.error("Fitur ini hanya untuk petugas");
      navigate("/");
      return;
    }
  }, [isLoggedIn, userRole, navigate]);
  
  // Load offline status
  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      // Check local IndexedDB status
      const localStatus = await isOfflineModeValid();
      setOfflineStatus(localStatus);
      
      // Get stats
      const offlineStats = await getOfflineStats();
      setStats(offlineStats);
      
      // Get pending payments
      const pending = await getPendingOfflinePayments();
      setPendingPayments(pending);
      
    } catch (error) {
      console.error("Error loading offline status:", error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    loadStatus();
  }, [loadStatus]);
  
  // Download data for offline
  const handleDownload = async () => {
    if (!online) {
      toast.error("Tidak ada koneksi internet. Hubungkan ke internet untuk mengunduh data.");
      return;
    }
    
    // BLOKIR: Jika masih ada pembayaran yang belum disinkronkan
    if (pendingPayments.length > 0) {
      toast.error(
        `Tidak bisa update data! Anda masih memiliki ${pendingPayments.length} pembayaran yang belum disinkronkan. Silakan sinkronkan terlebih dahulu.`,
        { duration: 5000 }
      );
      return;
    }
    
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/user/offline/download-bills`, {
        headers: getAuthHeader()
      });
      
      const data = response.data;
      
      if (!data.success) {
        throw new Error(data.message || "Gagal mengunduh data");
      }
      
      // Save to IndexedDB
      await saveBillsOffline(data.bills);
      await saveAdminFeeOffline(data.admin_fee);
      await saveOfflineSession(data.user_id, data.user_name, data.bills_count);
      
      toast.success(`Berhasil mengunduh ${data.bills_count} data tagihan untuk mode offline`);
      
      // Send notification
      if (notificationStatus.permission === 'granted') {
        await sendDownloadNotification(data.bills_count);
      }
      
      // Reload status
      await loadStatus();
      
    } catch (error) {
      console.error("Download error:", error);
      toast.error(error.response?.data?.detail || "Gagal mengunduh data offline");
    } finally {
      setDownloading(false);
    }
  };
  
  // Sync pending offline payments
  const handleSync = async () => {
    if (!online) {
      toast.error("Tidak ada koneksi internet. Hubungkan ke internet untuk sinkronisasi.");
      return;
    }
    
    if (pendingPayments.length === 0) {
      toast.info("Tidak ada pembayaran yang perlu disinkronkan");
      return;
    }
    
    setSyncing(true);
    try {
      // Format payments for sync
      const paymentsToSync = pendingPayments.map(p => ({
        offline_id: p.id,
        bill_ids: p.bill_ids,
        customer_no: p.customer_no,
        customer_name: p.customer_name,
        customer_address: p.customer_address || "",
        amount: p.amount,
        admin_fee: p.admin_fee,
        total: p.total,
        bill_details: p.bill_details || [],
        payment_method: p.payment_method || "",  // "cash" atau "transfer"
        created_at: p.created_at
      }));
      
      const response = await axios.post(
        `${API}/user/offline/sync-payments`,
        paymentsToSync,
        { headers: getAuthHeader() }
      );
      
      const result = response.data;
      
      // Mark synced payments
      for (const synced of result.synced) {
        await markPaymentSynced(synced.offline_id);
      }
      
      // Update balance in localStorage
      if (result.balance_after !== undefined) {
        localStorage.setItem("user_balance", result.balance_after);
      }
      
      // Calculate total amount synced
      const totalAmountSynced = pendingPayments
        .filter(p => result.synced.some(s => s.offline_id === p.id))
        .reduce((sum, p) => sum + p.total, 0);
      
      if (result.synced.length > 0) {
        toast.success(`Berhasil sinkronisasi ${result.synced.length} pembayaran`);
        
        // Send push notification for successful sync
        if (notificationStatus.permission === 'granted') {
          if (result.failed.length === 0) {
            await sendSyncSuccessNotification(result.synced.length, totalAmountSynced);
          } else {
            await sendSyncPartialNotification(result.synced.length, result.failed.length);
          }
        }
      }
      
      if (result.failed.length > 0) {
        toast.error(`${result.failed.length} pembayaran gagal disinkronkan`);
      }
      
      // Reload status
      await loadStatus();
      
    } catch (error) {
      console.error("Sync error:", error);
      toast.error(error.response?.data?.detail || "Gagal sinkronisasi pembayaran");
    } finally {
      setSyncing(false);
    }
  };
  
  // Clear offline data
  const handleClearData = async () => {
    if (!window.confirm("Apakah Anda yakin ingin menghapus semua data offline? Pembayaran yang belum disinkronkan akan hilang!")) {
      return;
    }
    
    try {
      await clearOfflineSession();
      toast.success("Data offline berhasil dihapus");
      await loadStatus();
    } catch (error) {
      toast.error("Gagal menghapus data offline");
    }
  };
  
  // Go to bills page for offline usage
  const goToBills = () => {
    navigate("/bills");
  };
  
  const today = getTodayDateString();
  const isValidToday = offlineStatus?.valid && offlineStatus?.date === today;
  
  return (
    <div className="min-h-screen hero-gradient">
      <header className="container mx-auto px-6 py-6">
        <nav className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/icons/icon-72x72.png" alt="Logo" className="w-10 h-10 rounded-xl" />
            <span className="text-xl font-heading font-semibold text-slate-800">Mode Offline</span>
          </div>
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Kembali
            </Button>
          </Link>
        </nav>
      </header>

      <main className="container mx-auto px-6 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Connection Status */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-xl p-4 mb-6 flex items-center gap-3 ${
              online 
                ? "bg-emerald-50 border border-emerald-200" 
                : "bg-amber-50 border border-amber-200"
            }`}
          >
            {online ? (
              <>
                <Wifi className="w-5 h-5 text-emerald-600" />
                <div>
                  <p className="font-semibold text-emerald-800">Terhubung ke Internet</p>
                  <p className="text-sm text-emerald-600">Anda dapat mengunduh data atau sinkronisasi pembayaran</p>
                </div>
              </>
            ) : (
              <>
                <WifiOff className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-semibold text-amber-800">Tidak Ada Koneksi</p>
                  <p className="text-sm text-amber-600">Mode offline aktif jika sudah mengunduh data hari ini</p>
                </div>
              </>
            )}
          </motion.div>

          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-sky-500 mb-4" />
              <p className="text-slate-600">Memuat status offline...</p>
            </div>
          ) : (
            <>
              {/* Offline Status Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <Card className={`mb-6 border-2 ${isValidToday ? "border-emerald-300 bg-emerald-50/50" : "border-slate-200"}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        isValidToday ? "bg-emerald-100" : "bg-slate-100"
                      }`}>
                        {isValidToday ? (
                          <Check className="w-6 h-6 text-emerald-600" />
                        ) : (
                          <CloudOff className="w-6 h-6 text-slate-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h2 className="text-lg font-heading font-semibold text-slate-800 mb-1">
                          {isValidToday ? "Mode Offline Aktif" : "Mode Offline Tidak Aktif"}
                        </h2>
                        {isValidToday ? (
                          <div className="space-y-2">
                            <p className="text-sm text-emerald-700">
                              Data berlaku untuk tanggal: <strong>{offlineStatus.date}</strong>
                            </p>
                            <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                              <span className="flex items-center gap-1">
                                <Database className="w-4 h-4" />
                                {stats?.totalBills || 0} tagihan
                              </span>
                              <span className="flex items-center gap-1">
                                <FileText className="w-4 h-4" />
                                {stats?.unpaidBills || 0} belum bayar
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {offlineStatus.downloadedAt ? new Date(offlineStatus.downloadedAt).toLocaleTimeString("id-ID") : "-"}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-slate-500">
                            {offlineStatus?.reason === 'expired' 
                              ? `Data terakhir: ${offlineStatus.lastDate}. Silakan unduh data baru untuk hari ini.`
                              : "Silakan unduh data terlebih dahulu untuk menggunakan mode offline."
                            }
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Pending Payments Alert */}
              {pendingPayments.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6"
                >
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold text-amber-800">
                        {pendingPayments.length} Pembayaran Belum Disinkronkan
                      </p>
                      <p className="text-sm text-amber-700 mb-3">
                        Total: {formatCurrency(pendingPayments.reduce((sum, p) => sum + p.total, 0))}
                      </p>
                      <Button
                        onClick={handleSync}
                        disabled={!online || syncing}
                        size="sm"
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                      >
                        {syncing ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Menyinkronkan...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Sinkronkan Sekarang
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="space-y-4"
              >
                {/* Warning: Must Sync Before Download */}
                {pendingPayments.length > 0 && online && (
                  <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="font-semibold text-red-800">
                          Wajib Sinkronisasi Sebelum Update Data!
                        </p>
                        <p className="text-sm text-red-700 mt-1">
                          Anda tidak dapat mengunduh/update data tagihan baru sebelum menyinkronkan {pendingPayments.length} pembayaran yang sudah lunas.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Download Button */}
                <Button
                  onClick={handleDownload}
                  disabled={!online || downloading || pendingPayments.length > 0}
                  className={`w-full h-14 rounded-xl text-lg font-semibold ${
                    pendingPayments.length > 0 
                      ? "bg-slate-300 text-slate-500 cursor-not-allowed" 
                      : "bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700"
                  }`}
                  data-testid="download-offline-btn"
                >
                  {downloading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Mengunduh Data...
                    </>
                  ) : pendingPayments.length > 0 ? (
                    <>
                      <AlertTriangle className="w-5 h-5 mr-2" />
                      Sinkronkan Dulu Sebelum Update
                    </>
                  ) : (
                    <>
                      <Download className="w-5 h-5 mr-2" />
                      {isValidToday ? "Update Data Offline" : "Download Data Offline"}
                    </>
                  )}
                </Button>

                {/* Go to Bills Button */}
                {isValidToday && (
                  <Button
                    onClick={goToBills}
                    variant="outline"
                    className="w-full h-14 rounded-xl text-lg font-semibold border-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    data-testid="go-bills-btn"
                  >
                    <FileText className="w-5 h-5 mr-2" />
                    Buka Tagihan PDAM
                  </Button>
                )}

                {/* Clear Data Button */}
                {(stats?.totalBills > 0 || pendingPayments.length > 0) && (
                  <Button
                    onClick={handleClearData}
                    variant="ghost"
                    className="hidden w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid="clear-offline-btn"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Hapus Data Offline
                  </Button>
                )}

                {/* Notification Permission Button */}
                {notificationStatus.supported && notificationStatus.permission !== 'granted' && (
                  <Button
                    onClick={async () => {
                      const granted = await requestNotificationPermission();
                      if (granted) {
                        setNotificationStatus({ ...notificationStatus, permission: 'granted' });
                        toast.success("Notifikasi berhasil diaktifkan!");
                      } else {
                        toast.error("Izin notifikasi ditolak. Anda dapat mengaktifkannya di pengaturan browser.");
                      }
                    }}
                    variant="outline"
                    className="w-full border-purple-200 text-purple-700 hover:bg-purple-50"
                    data-testid="enable-notification-btn"
                  >
                    <Bell className="w-4 h-4 mr-2" />
                    Aktifkan Notifikasi Sinkronisasi
                  </Button>
                )}

                {/* Notification Status */}
                {notificationStatus.supported && notificationStatus.permission === 'granted' && (
                  <div className="flex items-center justify-center gap-2 text-sm text-emerald-600">
                    <Bell className="w-4 h-4" />
                    <span>Notifikasi aktif - Anda akan diberitahu saat sinkronisasi selesai</span>
                  </div>
                )}

                {/* Auto-Sync Status (Always ON - Cannot be disabled) */}
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-emerald-100">
                      <Zap className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-emerald-800 flex items-center gap-2">
                        Auto-Sync Aktif
                        <span className="text-xs font-normal text-white bg-emerald-500 px-2 py-0.5 rounded-full">
                          SELALU ON
                        </span>
                      </p>
                      <p className="text-xs text-emerald-600">
                        Sinkronisasi otomatis setiap 30 detik saat online
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-emerald-200/50">
                    <div className="flex items-center text-xs">
                      <span className={`${pendingPayments.length > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                        {pendingPayments.length > 0 
                          ? `⚡ ${pendingPayments.length} pembayaran menunggu sinkronisasi`
                          : '✓ Semua pembayaran sudah tersinkron'
                        }
                      </span>
                    </div>
                    <p className="text-xs text-emerald-700 mt-2">
                      💡 Pembayaran otomatis masuk ke sistem saat HP terhubung internet. Tidak perlu menekan tombol apapun.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Instructions */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-8 bg-slate-50 rounded-xl p-5 border border-slate-200"
              >
                <h3 className="font-semibold text-slate-800 mb-3">Cara Menggunakan Mode Offline:</h3>
                <ol className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-sky-100 text-sky-700 text-xs flex items-center justify-center shrink-0 mt-0.5">1</span>
                    <span>Pastikan Anda terhubung ke internet</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-sky-100 text-sky-700 text-xs flex items-center justify-center shrink-0 mt-0.5">2</span>
                    <span>Klik tombol <strong>"Download Data Offline"</strong> untuk mengunduh data tagihan hari ini</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-sky-100 text-sky-700 text-xs flex items-center justify-center shrink-0 mt-0.5">3</span>
                    <span>Setelah download selesai, Anda bisa mengakses tagihan <strong>tanpa internet</strong> selama hari ini</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs flex items-center justify-center shrink-0 mt-0.5">4</span>
                    <span className="text-amber-700"><strong>Sebelum update data baru:</strong> Sinkronkan semua pembayaran yang sudah lunas terlebih dahulu</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-sky-100 text-sky-700 text-xs flex items-center justify-center shrink-0 mt-0.5">5</span>
                    <span>Setelah sinkronisasi, Anda bisa update data tagihan terbaru</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs flex items-center justify-center shrink-0 mt-0.5">!</span>
                    <span className="text-amber-700"><strong>Penting:</strong> Data offline hanya berlaku untuk hari ini. Besok Anda harus download ulang.</span>
                  </li>
                </ol>
                
                {/* Debug button for troubleshooting */}
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      const debug = await debugIndexedDB();
                      if (debug) {
                        alert(`Status IndexedDB:\n\n` +
                          `Session: ${debug.session ? 'Ada ('+debug.session.date+')' : 'Tidak ada'}\n` +
                          `Total Tagihan: ${debug.billsCount}\n` +
                          `Total Pembayaran Offline: ${debug.paymentsCount}\n\n` +
                          `Detail lihat di Console (F12)`);
                      } else {
                        alert('Gagal membaca IndexedDB');
                      }
                    }}
                    className="text-xs text-slate-500"
                    data-testid="debug-indexeddb-btn"
                  >
                    Debug IndexedDB
                  </Button>
                </div>
              </motion.div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
